
import React from 'react';
import { CONTACT_CONFIG, UI_CONFIG, FOOTER_CONFIG } from '../../config';
import { View, UserSettings } from '../../types';
import { BookOpenIcon } from './Icons';

interface FooterProps {
    user: UserSettings | null;
    onNavigate: (view: View) => void;
}

const Footer: React.FC<FooterProps> = ({ user, onNavigate }) => {
    const currentYear = new Date().getFullYear();
    const isLoggedIn = !!user;
    const isStudentCode = user?.authType === 'student_code';

    const renderLinks = (links: any[]) => {
        return (links || []).map((link, idx) => {
            // Check role visibility
            if (link.role === 'teacher' && user?.role !== 'teacher') return null;
            if (link.role === 'student' && user?.role !== 'student') return null;
            if (link.role === 'parent' && user?.role !== 'parent') return null;
            if (link.role === 'standard' && (!isLoggedIn || isStudentCode)) return null;

            const isExternal = link.view && (link.view.startsWith('http') || link.view.startsWith('/') || link.view.includes('.'));
            const target = link.target || '_self';

            if (isExternal) {
                return (
                    <li key={idx}>
                        <a 
                            href={link.view} 
                            target={target}
                            rel={target === '_blank' ? 'noopener noreferrer' : undefined}
                            className="text-slate-400 hover:text-white text-xs font-bold transition-colors text-left inline-block"
                        >
                            {link.label}
                        </a>
                    </li>
                );
            }

            return (
                <li key={idx}>
                    <button 
                        onClick={() => onNavigate(View[link.view as keyof typeof View] || View.Dashboard)} 
                        className="text-slate-400 hover:text-white text-xs font-bold transition-colors text-left"
                    >
                        {link.label}
                    </button>
                </li>
            );
        }).filter(Boolean);
    };

    const creativeLinks = renderLinks(FOOTER_CONFIG.CREATIVE_LINKS);
    const learningLinks = renderLinks(FOOTER_CONFIG.LEARNING_LINKS);
    const supportLinks = renderLinks(FOOTER_CONFIG.SUPPORT_LINKS);

    // Filter or modify support links based on login status
    const finalSupportLinks = (supportLinks || []).map((link: any) => {
        // Safely extract text from React element children (children can be string or nested element)
        const childText = typeof link.props?.children === 'string'
            ? link.props.children
            : (typeof link.props?.children?.props?.children === 'string' ? link.props.children.props.children : '');
        const hasUserGuideText = typeof childText === 'string' && childText.includes('Hướng dẫn');
        // If this is the "Hướng dẫn sử dụng" link (we'll identify it by label or view)
        // Note: The original config usually has ComingSoon for this.
        if (link.key?.includes('ComingSoon') || link.props?.children?.props?.onClick?.toString().includes('ComingSoon') || hasUserGuideText) {
            if (!isLoggedIn) {
                return (
                    <li key="about-app">
                        <button onClick={() => onNavigate(View.AboutApp)} className="text-slate-400 hover:text-white text-xs font-bold transition-colors text-left flex items-center gap-2 group">
                            <i className="ti ti-info-circle text-[10px] text-slate-600 group-hover:text-teal-400 transition-colors"></i> Giới thiệu ứng dụng
                        </button>
                    </li>
                );
            } else {
                return (
                    <li key="user-guide">
                        <button onClick={() => onNavigate(View.UserGuide)} className="text-slate-400 hover:text-white text-xs font-bold transition-colors text-left flex items-center gap-2 group">
                            <i className="ti ti-help-circle text-[10px] text-slate-600 group-hover:text-teal-400 transition-colors"></i> Hướng dẫn sử dụng
                        </button>
                    </li>
                );
            }
        }
        return link;
    });

    const hasCreative = creativeLinks.length > 0;
    const hasLearning = learningLinks.length > 0;
    const hasSupport = supportLinks.length > 0;

    // Count visible columns for layout balancing
    const visibleCols = [true, hasCreative, hasLearning, hasSupport].filter(Boolean).length;

    return (
        <footer className="bg-slate-900 text-slate-400 pt-20 pb-10 mt-20 relative overflow-hidden">
            {/* Decorative background elements */}
            <div className="absolute top-0 left-0 w-full h-full pointer-events-none opacity-20">
                <div className="absolute top-[-10%] right-[-10%] w-96 h-96 bg-teal-500/10 blur-[120px] rounded-full"></div>
                <div className="absolute bottom-[-10%] left-[-10%] w-96 h-96 bg-indigo-500/10 blur-[120px] rounded-full"></div>
            </div>

            {/* Top divider line */}
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-slate-700/50 to-transparent"></div>

            <div className="max-w-5xl mx-auto px-6 lg:px-8 relative z-10">
                {/* Row 1: Main Content Flex Layout */}
                <div className="flex flex-wrap lg:flex-nowrap gap-12 lg:gap-6 mb-16 justify-between">
                    {/* Brand & Mission Section - Occupies reasonable space */}
                    <div className="w-full lg:w-[32%] space-y-6">
                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center shadow-xl shadow-teal-500/20 rotate-3">
                                    {UI_CONFIG.APP_LOGO_URL ? (
                                        <img src={UI_CONFIG.APP_LOGO_URL} alt="Logo" className="w-9 sm:w-10 h-auto max-h-11 object-contain brightness-0 invert opacity-95" decoding="async" />
                                    ) : (
                                        <BookOpenIcon className="w-6 h-6 text-white" />
                                    )}
                                </div>
                                <span className="text-xl font-black text-white tracking-tighter uppercase">{UI_CONFIG.APP_NAME}</span>
                            </div>
                            {UI_CONFIG.APP_SUBTITLE && (
                                <p className="text-slate-400 text-[11px] leading-relaxed font-semibold">
                                    {UI_CONFIG.APP_SUBTITLE}
                                </p>
                            )}
                            {FOOTER_CONFIG.DESCRIPTION && (
                                <p className="text-slate-500 text-[10px] leading-relaxed font-medium max-w-sm">
                                    {FOOTER_CONFIG.DESCRIPTION}
                                </p>
                            )}
                        </div>

                        <div className="flex items-center gap-2.5">
                            {CONTACT_CONFIG.FACEBOOK_LINK && (
                                <a href={CONTACT_CONFIG.FACEBOOK_LINK} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-xl bg-slate-800/40 flex items-center justify-center text-slate-500 hover:text-white hover:bg-blue-600 transition-all border border-slate-800/50 group" title="Facebook">
                                    <i className="ti ti-brand-facebook text-base group-hover:scale-110 transition-transform"></i>
                                </a>
                            )}
                            {CONTACT_CONFIG.ZALO_LINK && (
                                <a href={CONTACT_CONFIG.ZALO_LINK} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-xl bg-slate-800/40 flex items-center justify-center text-slate-500 hover:text-white hover:bg-teal-600 transition-all border border-slate-800/50 group" title="Zalo">
                                    <i className="ti ti-brand-hipchat text-base group-hover:scale-110 transition-transform"></i>
                                </a>
                            )}
                            {CONTACT_CONFIG.YOUTUBE_LINK && (
                                <a href={CONTACT_CONFIG.YOUTUBE_LINK} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-xl bg-slate-800/40 flex items-center justify-center text-slate-500 hover:text-white hover:bg-red-600 transition-all border border-slate-800/50 group" title="Youtube">
                                    <i className="ti ti-brand-youtube text-base group-hover:scale-110 transition-transform"></i>
                                </a>
                            )}
                            {CONTACT_CONFIG.TIKTOK_LINK && (
                                <a href={CONTACT_CONFIG.TIKTOK_LINK} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-xl bg-slate-800/40 flex items-center justify-center text-slate-500 hover:text-white hover:bg-slate-700 transition-all border border-slate-800/50 group" title="TikTok">
                                    <i className="ti ti-brand-tiktok text-base group-hover:scale-110 transition-transform"></i>
                                </a>
                            )}
                            <a href={`mailto:${CONTACT_CONFIG.EMAIL}`} className="w-8 h-8 rounded-xl bg-slate-800/40 flex items-center justify-center text-slate-500 hover:text-white hover:bg-rose-600 transition-all border border-slate-800/50 group" title="Email Support">
                                <i className="ti ti-mail text-base group-hover:scale-110 transition-transform"></i>
                            </a>
                        </div>
                    </div>

                    {/* Columns Wrapper: Distributes remaining space evenly */}
                    <div className="flex-grow flex flex-wrap lg:flex-nowrap justify-between gap-10 lg:gap-6">
                        {/* Column 2: Creative Tools */}
                        {hasCreative && (
                            <div className="min-w-[140px]">
                                {FOOTER_CONFIG.CREATIVE_TITLE ? (
                                    <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-teal-400 mb-6 flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full bg-teal-500"></span>
                                        {FOOTER_CONFIG.CREATIVE_TITLE}
                                    </h4>
                                ) : (
                                    <div className="mb-6" aria-hidden />
                                )}
                                <ul className="space-y-3">
                                    {creativeLinks}
                                </ul>
                            </div>
                        )}

                        {/* Column 3: Learning */}
                        {hasLearning && (
                            <div className="min-w-[140px]">
                                {FOOTER_CONFIG.LEARNING_TITLE ? (
                                    <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-400 mb-6 flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
                                        {FOOTER_CONFIG.LEARNING_TITLE}
                                    </h4>
                                ) : (
                                    <div className="mb-6" aria-hidden />
                                )}
                                <ul className="space-y-3">
                                    {learningLinks}
                                </ul>
                            </div>
                        )}

                        {/* Column 4: Support & Policies */}
                        {hasSupport && (
                            <div className="min-w-[160px]">
                                {FOOTER_CONFIG.SUPPORT_TITLE ? (
                                    <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-rose-400 mb-6 flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                                        {FOOTER_CONFIG.SUPPORT_TITLE}
                                    </h4>
                                ) : (
                                    <div className="mb-6" aria-hidden />
                                )}
                                <ul className="space-y-3">
                                    {finalSupportLinks}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>

                {/* Row 2: Horizontal Contact Bar - More compact */}
                {(CONTACT_CONFIG.HOTLINE || CONTACT_CONFIG.EMAIL || CONTACT_CONFIG.ADDRESS) && (
                    <div className="py-5 border-t border-slate-800/40 flex flex-wrap justify-center items-center gap-x-10 gap-y-4 mb-8">
                        {CONTACT_CONFIG.HOTLINE && (
                            <div className="flex items-center gap-2.5 group">
                                <div className="w-6 h-6 rounded-lg bg-teal-500/10 flex items-center justify-center text-teal-500 group-hover:bg-teal-500 group-hover:text-white transition-all">
                                    <i className="ti ti-phone-call text-xs"></i>
                                </div>
                                <div>
                                    <p className="text-[7px] font-black uppercase text-slate-600 tracking-widest leading-none mb-1">Hotline 24/7</p>
                                    <a href={`tel:${String(CONTACT_CONFIG.HOTLINE).replace(/\s/g, '')}`} className="text-slate-200 text-xs font-black hover:text-white transition-colors">{CONTACT_CONFIG.HOTLINE}</a>
                                </div>
                            </div>
                        )}

                        {CONTACT_CONFIG.HOTLINE && CONTACT_CONFIG.EMAIL && <div className="hidden sm:block w-px h-6 bg-slate-800/60"></div>}

                        {CONTACT_CONFIG.EMAIL && (
                            <div className="flex items-center gap-2.5 group">
                                <div className="w-6 h-6 rounded-lg bg-indigo-500/10 flex items-center justify-center text-indigo-500 group-hover:bg-indigo-500 group-hover:text-white transition-all">
                                    <i className="ti ti-mail text-xs"></i>
                                </div>
                                <div>
                                    <p className="text-[7px] font-black uppercase text-slate-600 tracking-widest leading-none mb-1">Email doanh nghiệp</p>
                                    <a href={`mailto:${CONTACT_CONFIG.EMAIL}`} className="text-slate-300 text-[10px] font-bold hover:text-white transition-colors">{CONTACT_CONFIG.EMAIL}</a>
                                </div>
                            </div>
                        )}

                        {CONTACT_CONFIG.EMAIL && CONTACT_CONFIG.ADDRESS && <div className="hidden sm:block w-px h-6 bg-slate-800/60"></div>}

                        {CONTACT_CONFIG.ADDRESS && (
                            <div className="flex items-center gap-2.5 group">
                                <div className="w-6 h-6 rounded-lg bg-rose-500/10 flex items-center justify-center text-rose-500 group-hover:bg-rose-500 group-hover:text-white transition-all">
                                    <i className="ti ti-map-pin text-xs"></i>
                                </div>
                                <div>
                                    <p className="text-[7px] font-black uppercase text-slate-600 tracking-widest leading-none mb-1">Địa chỉ văn phòng</p>
                                    <p className="text-slate-400 text-[9px] font-medium leading-relaxed italic">{CONTACT_CONFIG.ADDRESS}</p>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Bottom Bar: Copyright & Tax */}
                <div className="pt-8 border-t border-slate-800/60 flex flex-col sm:flex-row justify-between items-center gap-6">
                    <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
                        {FOOTER_CONFIG.COPYRIGHT && (
                            <p className="text-slate-600 text-[9px] font-bold uppercase tracking-widest">
                                {FOOTER_CONFIG.COPYRIGHT}
                            </p>
                        )}
                        {FOOTER_CONFIG.COPYRIGHT && FOOTER_CONFIG.TAX_INFO && <div className="hidden sm:block w-1 h-1 rounded-full bg-slate-800"></div>}
                        {FOOTER_CONFIG.TAX_INFO && (
                            <p className="text-[8px] font-bold text-slate-700 uppercase tracking-widest">
                                {FOOTER_CONFIG.TAX_INFO}
                            </p>
                        )}
                    </div>
                    
                    {FOOTER_CONFIG.CRAFTED_BY && (
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-1.5 text-[9px] font-black text-slate-600 uppercase tracking-widest">
                                <span>Designed with</span>
                                <i className="ti ti-heart-filled text-rose-600 animate-pulse"></i>
                                <span>by</span>
                                <span className="text-slate-500 font-black">{FOOTER_CONFIG.CRAFTED_BY}</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </footer>
    );
};

export default Footer;
